import { AssessmentItemResponse } from '@app/models'
import { Timestamp } from 'firebase/firestore'

export enum FirestoreCollections {
  assessments = 'assessments',
  studentAssessments = 'studentAssessments',
  assessmentEvents = 'assessmentEvents',
  scoring = 'scoringByItem',
  testAdministrations = 'testAdministrations',
  students = 'students',
  scoringBySubtest = 'scoringBySubtest',
}

export enum AssessmentEventType {
  assessmentEvent = 'AssessmentEvent',
  assessmentItemEvent = 'AssessmentItemEvent',
}

export enum AssessmentActions {
  started = 'Started',
  paused = 'Paused',
  resumed = 'Resumed',
  restarted = 'Restarted',
  answered = 'Answered',
  reset = 'Reset',
  submitted = 'Submitted',
  skipped = 'Skipped',
  completed = 'Completed',
  timeAdded = 'TimeAdded',
  sync = 'Sync',
}

export enum AssessmentStatus {
  completed = 'COMPLETED',
  started = 'STARTED',
  new = 'NEW',
  interrupted = 'INTERRUPTED',
  paused = 'PAUSED',
}

export type AssessmentSettings = {
  timeLimit: number
  isResumable: boolean
  maxIdletime: number
  syncInterval: number
}

export type FirestoreAssessment = {
  id: string
  path: string
  deleted: boolean
  createdTimestamp: Date
  updatedTimestamp: Date
  settings: AssessmentSettings
}

export type UserAssessmentSettings = {
  timeLimits: number
  isResumable: boolean
}

export type UserAssessment = {
  userId: string
  assessmentId: string
  version: number
  itemResponses: AssessmentItemResponse[]
  lastViewedItemId: string
  createdTimestamp: Timestamp
  updatedTimestamp: Timestamp
  startedTimestamp: Timestamp
  attempt: number
  status: AssessmentStatus
  hideInfoBoxes: boolean
  deleted: boolean
  schoolYear: string
  assessmentSettings: UserAssessmentSettings
  passedTime: number
  addedTime: number
  responseXml: string
  //responseHash: string
}

export type AssessmentEvent = {
  id: string
  deleted: boolean
  userId: string
  createdTimestamp: Date
  updatedTimestamp: Date
  assessmentId: string
  assessmentItemId: string
  type: AssessmentEventType
  eventTimestamp: Date
  action: AssessmentActions
  attempt: number
  passedTime: number // seconds
  data: any /* Object with custom data (added time etc.) */
}

export enum TestAdministrationTimingOptions {
  STANDARD = 'standard',
  TIME_AND_A_HALF = 'time-and-half',
  DOUBLE = 'double',
  CUSTOM = 'custom',
  UNTIMED = 'untimed',
}

export enum TestAdministrationTimingMultipliers {
  STANDARD = 1,
  TIME_AND_A_HALF = 1.5,
  DOUBLE = 2,
  CUSTOM = null,
  UNTIMED = 0,
}

export type TestAdministrationSettings = {
  timing?: TestAdministrationTimingOptions
  customTimePercentage?: number // Used only if timing is set to CUSTOM
}

export type TestAdministration = {
  id: string
  name?: string
  description?: string
  settings: TestAdministrationSettings
  createdTimestamp: Timestamp
  updatedTimestamp: Timestamp
  deleted: boolean
}

export type StudentTimeAccommodation = {
  customTime: {
    enabled: boolean
    settings: {
      value: number
    } | null
  }
  doubleTime: {
    enabled: boolean
    settings: null
  }
  noTimingAccomdations: {
    enabled: boolean
    settings: null
  }
  timeAndHalf: {
    enabled: boolean
    settings: null
  }
  unlimitedTime: {
    enabled: boolean
    settings: null
  }
}

export type Student = {
  id: string
  timeAccomodation?: StudentTimeAccommodation
  // Other student fields can be added here as needed
}

export type SubScore = {
  name: string
  raw: string | number
  scale: string | number
}

export type ScoringBySubtestDoc = {
  bySubtest?: {
    scale: string | number
    subScores?: SubScore[]
  }
  calculation_status?: string
  complete?: boolean
}
